export interface CollapsibleGraph {
  nodes: { name: string; color?: string }[]
  links: { source: number; target: number; value: number }[]
}

// Bundles the smallest terminal recipients of a sankey into a single
// "Other (N more)" node so the busiest column fits `maxColumnNodes` rows. A
// terminal (leaf) node only ever receives flow, never sends it, e.g. the
// payees down the right side of an outgoing-money diagram. Returns the graph
// unchanged when it already fits, when there's nothing safe to bundle, or when
// bundling would merge fewer than two nodes (no point). Totals are preserved:
// every bundled link is redirected into "Other", summed per source so the
// diagram shows one band per source.
export function collapseSankeyToFit(
  graph: CollapsibleGraph,
  maxColumnNodes: number,
): CollapsibleGraph {
  if (maxColumnNodes < 2) return graph

  const sourceIndices = new Set(graph.links.map((link) => link.source))
  const incomingValue = new Map<number, number>()
  for (const link of graph.links) {
    incomingValue.set(
      link.target,
      (incomingValue.get(link.target) ?? 0) + link.value,
    )
  }

  const leafIndices = graph.nodes
    .map((_node, index) => index)
    .filter((index) => !sourceIndices.has(index) && incomingValue.has(index))

  if (leafIndices.length <= maxColumnNodes) return graph

  // Keep the largest leaves; everything past the cap (minus one slot for the
  // "Other" node) gets bundled. Bundling must merge at least two leaves.
  const keepCount = maxColumnNodes - 1
  const orderedLeaves = [...leafIndices].sort(
    (first, second) =>
      (incomingValue.get(second) ?? 0) - (incomingValue.get(first) ?? 0),
  )
  const bundledLeaves = new Set(orderedLeaves.slice(keepCount))
  if (bundledLeaves.size < 2) return graph

  // Rebuild the node list without the bundled leaves, appending one "Other".
  const oldToNew = new Map<number, number>()
  const nodes: CollapsibleGraph['nodes'] = []
  graph.nodes.forEach((node, index) => {
    if (bundledLeaves.has(index)) return
    oldToNew.set(index, nodes.length)
    nodes.push(node)
  })
  const otherIndex = nodes.length
  nodes.push({ name: `Other (${bundledLeaves.size} more)` })

  // Redirect every link that fed a bundled leaf into "Other", summing the
  // flows that share a source. Bundled nodes are leaves, so they never appear
  // as a link source and only their incoming links need rerouting.
  const mergedOtherLinks = new Map<number, number>()
  const links: CollapsibleGraph['links'] = []
  for (const link of graph.links) {
    if (bundledLeaves.has(link.target)) {
      const source = oldToNew.get(link.source)
      if (source === undefined) continue
      mergedOtherLinks.set(
        source,
        (mergedOtherLinks.get(source) ?? 0) + link.value,
      )
      continue
    }
    const source = oldToNew.get(link.source)
    const target = oldToNew.get(link.target)
    if (source === undefined || target === undefined) continue
    links.push({ source, target, value: link.value })
  }
  for (const [source, value] of mergedOtherLinks) {
    links.push({ source, target: otherIndex, value })
  }

  return { nodes, links }
}

// Estimates how many node rows fit in one sankey column at the given height.
// Each row needs room for a two-line label (name + value), so we budget a
// minimum vertical slice per node and floor at two (always keep at least the
// top flow plus an "Other" bucket).
const MIN_PX_PER_NODE = 34

export function maxSankeyColumnNodes(
  height: number,
  verticalMargin: number,
): number {
  const usable = height - verticalMargin
  return Math.max(2, Math.floor(usable / MIN_PX_PER_NODE))
}
